import api from './axios';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

/**
 * Bitbucket OAuth API functions
 */

export const initiateOAuth = async () => {
  const response = await api.get('/bitbucket/oauth/authorize');
  return response.data;
};

export const getConnectionStatus = async () => {
  const response = await api.get('/bitbucket/oauth/status');
  return response.data;
};

export const disconnectBitbucket = async () => {
  const response = await api.post('/bitbucket/oauth/disconnect');
  return response.data;
};

export const syncActivity = async () => {
  const response = await api.post('/bitbucket/oauth/sync');
  return response.data;
};

export const syncAllDevelopers = async () => {
  const response = await api.post('/bitbucket/oauth/sync-all');
  return response.data;
};

/**
 * React Query Hooks
 */

// Get OAuth connection status
export const useConnectionStatus = () => {
  return useQuery({
    queryKey: ['bitbucketConnection'],
    queryFn: getConnectionStatus,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1
  });
};

// Initiate OAuth flow
export const useInitiateOAuth = () => {
  return useMutation({
    mutationFn: initiateOAuth,
    onSuccess: (data) => {
      // Redirect to Bitbucket OAuth authorization page
      window.location.href = data.auth_url;
    },
    onError: (error) => {
      console.error('OAuth initiation failed:', error);
    }
  });
};

// Disconnect Bitbucket
export const useDisconnect = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: disconnectBitbucket,
    onSuccess: () => {
      // Invalidate connection status and activity queries
      queryClient.invalidateQueries({ queryKey: ['bitbucketConnection'] });
      queryClient.invalidateQueries({ queryKey: ['myActivity'] });
      queryClient.invalidateQueries({ queryKey: ['myCommits'] });
    }
  });
};

// Sync activity from Bitbucket
export const useSyncActivity = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: syncActivity,
    onSuccess: (data) => {
      // Invalidate and refetch activity data AND connection status to update last_synced
      queryClient.invalidateQueries({ queryKey: ['myActivity'] });
      queryClient.invalidateQueries({ queryKey: ['myCommits'] });
      queryClient.invalidateQueries({ queryKey: ['myRepos'] });
      queryClient.invalidateQueries({ queryKey: ['bitbucketConnection'] });
      
      alert(`✅ Sync Complete!\n\n📊 Stats:\n• ${data.commits_synced || 0} commits\n• ${data.prs_synced || 0} pull requests\n• ${data.repos_synced || 0} repositories`);
    },
    onError: (error) => {
      // Invalidate connection status to refresh UI (might be disconnected now)
      queryClient.invalidateQueries({ queryKey: ['bitbucketConnection'] });
      
      const errorMsg = error.response?.data?.message || error.message;
      
      // Check if it's a token error
      if (errorMsg.includes('401') || errorMsg.includes('token') || errorMsg.includes('reconnect')) {
        alert(`❌ Sync Failed - Token Expired\n\nYour Bitbucket connection has expired.\n\nPlease reconnect your Bitbucket account to continue syncing.`);
      } else {
        alert(`❌ Sync failed\n\n${errorMsg}`);
      }
    }
  });
};

// Sync all connected developers - for managers/admins
export const useSyncAllDevelopers = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: syncAllDevelopers,
    onSuccess: (data) => {
      // Invalidate team activity to refresh all developer data
      queryClient.invalidateQueries({ queryKey: ['teamActivity'] });
      
      const successMsg = data.results
        .filter(r => r.success)
        .map(r => `✅ ${r.name}: ${r.commits_synced || 0} commits, ${r.prs_synced || 0} PRs`)
        .join('\n');
      
      const reconnectMsg = data.results
        .filter(r => r.needs_reconnect)
        .map(r => `🔄 ${r.name}: Needs to reconnect Bitbucket`)
        .join('\n');
      
      const otherFailedMsg = data.results
        .filter(r => !r.success && !r.needs_reconnect)
        .map(r => `❌ ${r.name}: ${r.error}`)
        .join('\n');
      
      let message = `🔄 Sync All Complete!\n\n${data.message}\n\n`;
      if (successMsg) message += successMsg + '\n';
      if (reconnectMsg) message += '\n' + reconnectMsg + '\n';
      if (otherFailedMsg) message += '\n' + otherFailedMsg;
      
      alert(message);
    },
    onError: (error) => {
      alert(`❌ Sync All Failed\n\n${error.response?.data?.message || error.message}`);
    }
  });
};
